// =============================================================================
// BP-08-05: Vendor Portal Frontend - API Client
// =============================================================================

import type {
  VendorProfile,
  VendorProfileFormData,
  VendorProduct,
  ProductFormData,
  ProductListParams,
  ProductListResponse,
  VendorOrder,
  OrderListParams,
  OrderListResponse,
  OrderStatusUpdate,
  VendorReview,
  ReviewListParams,
  ReviewListResponse,
  VendorAnalytics,
  AnalyticsTimeframe,
  VendorStats,
  RecentActivity,
  ImageUploadResponse,
  ApiResponse,
} from '@/features/vendor/types/vendorTypes';

// Normalize API base URL to always end with /api/v1 (avoid duplicating it)
const rawApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${rawApiUrl.replace(/\/+$/, '').replace(/\/api\/v1$/, '')}/api/v1`;

// =============================================================================
// Utility Functions
// =============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new Error(error.message || error.detail || 'Request failed');
  }
  return response.json();
}

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// =============================================================================
// Profile API
// =============================================================================

export const vendorProfileApi = {
  /**
   * Get current vendor profile
   */
  async getProfile(): Promise<VendorProfile> {
    const response = await fetch(`${API_BASE}/vendor/profile`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorProfile>(response);
  },

  /**
   * Update vendor profile
   */
  async updateProfile(data: Partial<VendorProfileFormData>): Promise<VendorProfile> {
    const response = await fetch(`${API_BASE}/vendor/profile`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<VendorProfile>(response);
  },

  /**
   * Upload vendor logo
   */
  async uploadLogo(file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE}/vendor/profile/logo`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<ImageUploadResponse>(response);
  },

  /**
   * Upload vendor banner
   */
  async uploadBanner(file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE}/vendor/profile/banner`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<ImageUploadResponse>(response);
  },

  /**
   * Submit verification documents
   */
  async submitVerification(files: File[], documentType: string): Promise<ApiResponse<void>> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('document_type', documentType);
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE}/vendor/verify`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<ApiResponse<void>>(response);
  },

  /**
   * Get vendor statistics
   */
  async getStats(): Promise<VendorStats> {
    const response = await fetch(`${API_BASE}/vendor/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorStats>(response);
  },

  /**
   * Get recent activity
   */
  async getRecentActivity(limit = 10): Promise<RecentActivity[]> {
    const response = await fetch(`${API_BASE}/vendor/activity?limit=${limit}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<RecentActivity[]>(response);
  },
};

// =============================================================================
// Products API
// =============================================================================

export const vendorProductsApi = {
  /**
   * List vendor products
   */
  async list(params: ProductListParams = {}): Promise<ProductListResponse> {
    const query = buildQueryString({
      page: params.page || 1,
      page_size: params.pageSize || 20,
      status: params.status !== 'all' ? params.status : undefined,
      category_id: params.categoryId,
      search: params.search,
      sort_by: params.sortBy,
      sort_order: params.sortOrder,
    });
    
    const response = await fetch(`${API_BASE}/vendor/products${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ProductListResponse>(response);
  },

  /**
   * Get single product
   */
  async get(productId: string): Promise<VendorProduct> {
    const response = await fetch(`${API_BASE}/vendor/products/${productId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorProduct>(response);
  },

  /**
   * Create product
   */
  async create(data: ProductFormData): Promise<VendorProduct> {
    const response = await fetch(`${API_BASE}/vendor/products`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<VendorProduct>(response);
  },

  /**
   * Update product
   */
  async update(productId: string, data: Partial<ProductFormData>): Promise<VendorProduct> {
    const response = await fetch(`${API_BASE}/vendor/products/${productId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse<VendorProduct>(response);
  },

  /**
   * Delete product
   */
  async delete(productId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/vendor/products/${productId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete' }));
      throw new Error(error.message);
    }
  },

  /**
   * Upload product images
   */
  async uploadImages(productId: string, files: File[]): Promise<ImageUploadResponse[]> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_BASE}/vendor/products/${productId}/images`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    return handleResponse<ImageUploadResponse[]>(response);
  },

  /**
   * Delete product image
   */
  async deleteImage(productId: string, imageUrl: string): Promise<void> {
    const response = await fetch(`${API_BASE}/vendor/products/${productId}/images`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
      body: JSON.stringify({ image_url: imageUrl }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete image' }));
      throw new Error(error.message);
    }
  },

  /**
   * Update product status
   */
  async updateStatus(productId: string, status: VendorProduct['status']): Promise<VendorProduct> {
    return vendorProductsApi.update(productId, { status });
  },
};

// =============================================================================
// Orders API
// =============================================================================

export const vendorOrdersApi = {
  /**
   * List vendor orders
   */
  async list(params: OrderListParams = {}): Promise<OrderListResponse> {
    const query = buildQueryString({
      page: params.page || 1,
      page_size: params.pageSize || 20,
      status: params.status !== 'all' ? params.status : undefined,
      date_from: params.dateFrom,
      date_to: params.dateTo,
      search: params.search,
      sort_by: params.sortBy,
      sort_order: params.sortOrder,
    });
    
    const response = await fetch(`${API_BASE}/vendor/orders${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<OrderListResponse>(response);
  },

  /**
   * Get single order
   */
  async get(orderId: string): Promise<VendorOrder> {
    const response = await fetch(`${API_BASE}/vendor/orders/${orderId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorOrder>(response);
  },

  /**
   * Update order status
   */
  async updateStatus(orderId: string, update: OrderStatusUpdate): Promise<VendorOrder> {
    const response = await fetch(`${API_BASE}/vendor/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(update),
    });
    return handleResponse<VendorOrder>(response);
  },

  /**
   * Mark order as shipped
   */
  async ship(orderId: string, trackingNumber: string): Promise<VendorOrder> {
    const response = await fetch(`${API_BASE}/vendor/orders/${orderId}/ship`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ tracking_number: trackingNumber }),
    });
    return handleResponse<VendorOrder>(response);
  },

  /**
   * Confirm order
   */
  async confirm(orderId: string): Promise<VendorOrder> {
    return vendorOrdersApi.updateStatus(orderId, { status: 'confirmed' });
  },

  /**
   * Mark order as delivered
   */
  async deliver(orderId: string): Promise<VendorOrder> {
    return vendorOrdersApi.updateStatus(orderId, { status: 'delivered' });
  },

  /**
   * Cancel order
   */
  async cancel(orderId: string, reason: string): Promise<VendorOrder> {
    return vendorOrdersApi.updateStatus(orderId, { 
      status: 'cancelled', 
      vendorNotes: reason 
    });
  },
};

// =============================================================================
// Reviews API
// =============================================================================

export const vendorReviewsApi = {
  /**
   * List vendor reviews
   */
  async list(params: ReviewListParams = {}): Promise<ReviewListResponse> {
    const query = buildQueryString({
      page: params.page || 1,
      page_size: params.pageSize || 20,
      rating: params.rating !== 'all' ? params.rating : undefined,
      has_response: params.hasResponse,
      product_id: params.productId,
      sort_by: params.sortBy,
      sort_order: params.sortOrder,
    });
    
    const response = await fetch(`${API_BASE}/vendor/reviews${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<ReviewListResponse>(response);
  },

  /**
   * Respond to a review
   */
  async respond(reviewId: string, response: string): Promise<VendorReview> {
    const res = await fetch(`${API_BASE}/vendor/reviews/${reviewId}/respond`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ response }),
    });
    return handleResponse<VendorReview>(res);
  },

  /**
   * Update review response
   */
  async updateResponse(reviewId: string, response: string): Promise<VendorReview> {
    const res = await fetch(`${API_BASE}/vendor/reviews/${reviewId}/respond`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify({ response }),
    });
    return handleResponse<VendorReview>(res);
  },

  /**
   * Delete review response
   */
  async deleteResponse(reviewId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/vendor/reviews/${reviewId}/respond`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete' }));
      throw new Error(error.message);
    }
  },
};

// =============================================================================
// Analytics API
// =============================================================================

export const vendorAnalyticsApi = {
  /**
   * Get vendor analytics
   */
  async get(timeframe: AnalyticsTimeframe): Promise<VendorAnalytics> {
    const query = buildQueryString({
      start_date: timeframe.startDate,
      end_date: timeframe.endDate,
      granularity: timeframe.granularity,
    });
    
    const response = await fetch(`${API_BASE}/vendor/analytics${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorAnalytics>(response);
  },

  /**
   * Get views analytics
   */
  async getViews(timeframe: AnalyticsTimeframe): Promise<VendorAnalytics['viewsOverTime']> {
    const query = buildQueryString(timeframe);
    const response = await fetch(`${API_BASE}/vendor/analytics/views${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorAnalytics['viewsOverTime']>(response);
  },

  /**
   * Get inquiries analytics
   */
  async getInquiries(timeframe: AnalyticsTimeframe): Promise<VendorAnalytics['inquiriesOverTime']> {
    const query = buildQueryString(timeframe);
    const response = await fetch(`${API_BASE}/vendor/analytics/inquiries${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorAnalytics['inquiriesOverTime']>(response);
  },

  /**
   * Get revenue analytics
   */
  async getRevenue(timeframe: AnalyticsTimeframe): Promise<VendorAnalytics['revenueOverTime']> {
    const query = buildQueryString(timeframe);
    const response = await fetch(`${API_BASE}/vendor/analytics/revenue${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorAnalytics['revenueOverTime']>(response);
  },

  /**
   * Get top products
   */
  async getTopProducts(limit = 10): Promise<VendorAnalytics['topProducts']> {
    const response = await fetch(`${API_BASE}/vendor/analytics/top-products?limit=${limit}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorAnalytics['topProducts']>(response);
  },
};

// =============================================================================
// Categories API (for product forms)
// =============================================================================

export interface VendorCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
  parentId: string | null;
  children?: VendorCategory[];
}

export const vendorCategoriesApi = {
  /**
   * List all categories
   */
  async list(): Promise<VendorCategory[]> {
    const response = await fetch(`${API_BASE}/marketplace/categories`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<VendorCategory[]>(response);
  },
};

// =============================================================================
// Diagnoses API (for product targeting)
// =============================================================================

export interface Diagnosis {
  id: string;
  slug: string;
  name: string;
  shortName: string;
}

export const diagnosesApi = {
  /**
   * List all diagnoses
   */
  async list(): Promise<Diagnosis[]> {
    const response = await fetch(`${API_BASE}/diagnoses`, {
      headers: getAuthHeaders(),
    });
    return handleResponse<Diagnosis[]>(response);
  },
};

// =============================================================================
// Export all APIs
// =============================================================================

export const vendorApi = {
  profile: vendorProfileApi,
  products: vendorProductsApi,
  orders: vendorOrdersApi,
  reviews: vendorReviewsApi,
  analytics: vendorAnalyticsApi,
  categories: vendorCategoriesApi,
  diagnoses: diagnosesApi,
};

export default vendorApi;

